import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { displayCurrency } from "../utils/pricing";

// KIND → branded label. Never leaks provider names into the UI.
const KIND_LABEL = {
    heygen: "AI Trainer clip",
    trainer: "AI Trainer clip",
    runway: "Cinematic clip",
    cinematic: "Cinematic clip",
    voice: "Voice narration",
    motion: "Motion graphic",
    stitch: "Final stitch",
};

const KIND_PILL = {
    "AI Trainer clip": "bg-indigo-50 text-indigo-700",
    "Cinematic clip": "bg-amber-50 text-amber-800",
    "Voice narration": "bg-emerald-50 text-emerald-700",
    "Motion graphic": "bg-sky-50 text-sky-700",
    "Final stitch": "bg-neutral-100 text-neutral-700",
};

// Raw-cost helper for admin "Show pricing detail" mode. Same decimals
// as displayCurrency but WITHOUT the 1.5x multiplier. Sub-cent amounts
// get 7 decimals so the ratio holds at display precision.
const rawCurrency = (n, digits = 2) => {
    const v = Number.isFinite(n) ? n : 0;
    let d = digits;
    if (v > 0 && Math.abs(v) < 0.01) d = Math.max(d, 7);
    return v.toFixed(d);
};

export default function Usage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = user?.role === "ORG_ADMIN" || user?.role === "LD_ADMIN"
                    || user?.role === "SUPER_ADMIN";
    // Admin "Show pricing detail" toggle. Per-session, not persisted.
    // Also honours the ``?debug=1`` URL flag from Cycle 6 so scripted
    // audits keep working via permalink.
    const [showRaw, setShowRaw] = useState(
        isAdmin && searchParams.get("debug") === "1",
    );
    const verifyMode = isAdmin && showRaw;

    useEffect(() => {
        api.get("/usage/summary")
            .then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="p-8 text-sm text-neutral-500" data-testid="usage-loading">
                Loading your usage…
            </div>
        );
    }
    if (!data) {
        return (
            <div className="p-8 text-sm text-neutral-500" data-testid="usage-empty">
                No usage data yet.
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-6" data-testid="usage-page">
            <div>
                <h1 className="text-2xl font-semibold text-neutral-900">Usage</h1>
                <p className="mt-1 text-sm text-neutral-500" data-testid="usage-disclaimer">
                    All prices shown are what is charged to your account.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="rounded-lg border border-neutral-200 bg-white p-5"
                    data-testid="usage-mtd-card">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Month-to-date charged</div>
                    <div className="mono text-3xl mt-2 text-neutral-900" data-testid="usage-mtd-value">
                        ${displayCurrency(data.mtd_raw_usd || 0)}
                    </div>
                    {verifyMode && (
                        <div className="text-[11px] text-neutral-500 mt-1 mono" data-testid="usage-mtd-raw">
                            cost ${rawCurrency(data.mtd_raw_usd || 0)}
                        </div>
                    )}
                    <div className="text-xs text-neutral-500 mt-1">
                        {data.mtd_generation_count || 0} generations this month
                    </div>
                </div>
                <div className="rounded-lg border border-neutral-200 bg-white p-5"
                    data-testid="usage-today-card">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Today's charged</div>
                    <div className="mono text-3xl mt-2 text-neutral-900" data-testid="usage-today-value">
                        ${displayCurrency(data.today_raw_usd || 0)}
                    </div>
                    {verifyMode && (
                        <div className="text-[11px] text-neutral-500 mt-1 mono" data-testid="usage-today-raw">
                            cost ${rawCurrency(data.today_raw_usd || 0)}
                        </div>
                    )}
                    <div className="text-xs text-neutral-500 mt-1">
                        {data.today_generation_count || 0} generations today
                    </div>
                </div>
                <div className="rounded-lg border border-neutral-200 bg-white p-5"
                    data-testid="usage-modules-card">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">Modules built this month</div>
                    <div className="mono text-3xl mt-2 text-neutral-900" data-testid="usage-modules-value">
                        {data.mtd_module_count || 0}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                        Across your organisation
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <div className="border-b border-neutral-200 px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="text-sm font-semibold text-neutral-900">Recent charges</div>
                    <div className="flex items-center gap-4">
                        {verifyMode && (
                            <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest text-neutral-500">
                                <span>Cost</span>
                                <span className="text-neutral-900">
                                    Charged
                                    <span
                                        title="Includes standard platform pricing."
                                        className="ml-1.5 inline-flex items-center rounded bg-indigo-50 px-1.5 py-0.5 text-[9px] font-semibold text-indigo-700"
                                        data-testid="charged-header-badge"
                                    >
                                        ×1.5
                                    </span>
                                </span>
                            </div>
                        )}
                        {isAdmin && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setShowRaw((v) => !v)}
                                data-testid="show-pricing-detail-btn"
                                className="text-xs h-7"
                            >
                                {showRaw ? "Hide pricing detail" : "Show pricing detail"}
                            </Button>
                        )}
                    </div>
                </div>
                {verifyMode && (
                    <div
                        data-testid="pricing-detail-note"
                        className="border-b border-neutral-100 bg-indigo-50/40 px-5 py-2 text-[11px] text-indigo-900"
                    >
                        Every charge is base cost × 1.5 (standard platform pricing).
                    </div>
                )}
                {!data.recent_charges?.length ? (
                    <div className="p-6 text-sm text-neutral-500" data-testid="usage-recent-empty">
                        No completed generations yet — build a module to see charges here.
                    </div>
                ) : (
                    <div className="divide-y divide-neutral-100"
                        data-testid="usage-recent-charges">
                        {data.recent_charges.map((c, i) => {
                            const label = KIND_LABEL[c.kind] || c.kind || "Generation";
                            const pill = KIND_PILL[label] || "bg-neutral-100 text-neutral-700";
                            const ts = c.timestamp ? new Date(c.timestamp).toLocaleString() : "";
                            return (
                                <div key={i} className="flex items-center justify-between px-5 py-3"
                                    data-testid={`usage-charge-${i}`}>
                                    <div className="flex flex-col gap-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded ${pill}`}>
                                                {label}
                                            </span>
                                            <span className="text-sm text-neutral-900 truncate">
                                                {c.module_name || "—"}
                                            </span>
                                        </div>
                                        <div className="text-xs text-neutral-500">{ts}</div>
                                    </div>
                                    <div className="flex items-center gap-4 tabular-nums">
                                        {verifyMode && (
                                            <div className="mono text-xs text-neutral-500"
                                                data-testid={`usage-charge-raw-${i}`}>
                                                ${rawCurrency(c.raw_cost_usd || 0, 4)}
                                            </div>
                                        )}
                                        <div className="mono text-sm text-neutral-900"
                                            data-testid={`usage-charge-amount-${i}`}>
                                            ${displayCurrency(c.raw_cost_usd || 0, 4)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
